import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, CheckCircle2, AlertTriangle, ChevronRight, XCircle, Layers3, Zap, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ContactSalesModal from "@/components/ContactSalesModal";
import BrandLogo from "@/components/BrandLogo";
import { useTranslation } from "react-i18next";
import { sendResiliencePlanByEmail, trackAnalyticsEvent } from "@/services/api/client";

const QUESTIONS_KEYS = ["q1", "q2", "q3", "q4", "q5"] as const;
const FEEDBACK_KEYS = ["f1", "f2", "f3", "f4", "f5"] as const;

type Answer = "yes" | "no" | null;

const Event = () => {
  const { t, i18n } = useTranslation();
  const [started, setStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([null, null, null, null, null]);
  const [showResults, setShowResults] = useState(false);
  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [emailFormOpen, setEmailFormOpen] = useState(false);
  const [emailPro, setEmailPro] = useState("");
  const [name, setName] = useState("");
  const [companySize, setCompanySize] = useState("");
  const [wantsContact, setWantsContact] = useState(true);
  const [sendingPlan, setSendingPlan] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState(false);

  const primaryRiskKey = (() => {
    const firstNoIdx = answers.findIndex((a) => a === "no");
    if (firstNoIdx < 0) return null;
    return `r${firstNoIdx + 1}` as const;
  })();

  const handleAnswer = (answer: Answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQ] = answer;
    setAnswers(newAnswers);

    if (currentQ < 4) {
      setTimeout(() => setCurrentQ(currentQ + 1), 300);
    } else {
      setTimeout(() => setShowResults(true), 400);
    }
  };

  const riskCount = answers.filter((a) => a === "no").length;
  const isAtRisk = riskCount >= 2;
  const scorePercent = Math.round(((5 - riskCount) / 5) * 100);

  useEffect(() => {
    if (!showResults) return;
    const yesCount = answers.filter((a) => a === "yes").length;
    void trackAnalyticsEvent("resilience_test_completed", "event", {
      scorePercent,
      yesProportion: yesCount / 5,
      noProportion: riskCount / 5,
    });
  }, [showResults, scorePercent, riskCount, answers]);

  const reset = () => {
    setStarted(false);
    setCurrentQ(0);
    setAnswers([null, null, null, null, null]);
    setShowResults(false);
  };

  const handleSendPlan = async () => {
    setSendError("");
    const email = emailPro.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSendError(String(t("event.results.plan.form.invalidEmail")));
      return;
    }
    try {
      setSendingPlan(true);
      await sendResiliencePlanByEmail({
        email,
        name: name.trim() || undefined,
        companySize: companySize.trim() || undefined,
        wantsContact,
        lang: i18n.language?.slice(0, 2) === "fr" ? "fr" : "en",
        scorePercent,
        questionAnswers: QUESTIONS_KEYS.map((key, i) => ({
          question: String(t(`event.test.${key}`)),
          answer: answers[i] === "yes" ? "yes" : "no",
        })),
      });
      setSendSuccess(true);
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(t("event.results.plan.form.sendFailed")));
    } finally {
      setSendingPlan(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <ContactSalesModal open={salesModalOpen} onOpenChange={setSalesModalOpen} analyticsPage="event" />
      <Dialog open={planModalOpen} onOpenChange={setPlanModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0 sm:rounded-lg">
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain px-6 pt-6 pb-6 touch-pan-y [scrollbar-gutter:stable]">
            <DialogHeader>
              <DialogTitle className="text-xl md:text-2xl">
                {t("event.results.plan.title")}
              </DialogTitle>
              <DialogDescription className="whitespace-pre-line">
                {t("event.results.plan.subtitle")}
              </DialogDescription>
            </DialogHeader>

            <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            className="space-y-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.05 }}
              className="rounded-xl border border-destructive/30 bg-destructive/10 p-4"
            >
              <div className="font-semibold text-destructive flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                {t("event.results.plan.important")}
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-destructive/90">
                {(
                  t("event.results.plan.importantImpacts", {
                    returnObjects: true,
                    defaultValue: [],
                  }) as string[]
                ).map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.1 }}
              className="space-y-3"
            >
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Layers3 className="h-4 w-4 text-primary shrink-0" />
                {t("event.results.plan.stepsTitle")}
              </h3>
              <div className="grid gap-3 md:grid-cols-3">
                {(["s1", "s2", "s3"] as const).map((k) => (
                  <div key={k} className="rounded-xl border border-border bg-card p-4">
                    <div className="text-sm font-semibold text-foreground">
                      {t(`event.results.plan.steps.${k}.title`)}
                    </div>
                    <div className="mt-2 text-sm text-muted-foreground">
                      {t(`event.results.plan.steps.${k}.desc`)}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.14 }}
              className="space-y-3"
            >
              <h3 className="font-semibold text-foreground flex items-center gap-2">
                <Zap className="h-4 w-4 text-primary shrink-0" />
                {t("event.results.plan.beforeAfterTitle")}
              </h3>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
                  <div className="font-semibold text-foreground">
                    {t("event.results.plan.before.title", { score: scorePercent })}
                  </div>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {(
                      t("event.results.plan.before.items", { returnObjects: true, defaultValue: [] }) as string[]
                    ).map((it, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-xl border border-accent/20 bg-accent/5 p-4">
                  <div className="font-semibold text-foreground">{t("event.results.plan.after.title")}</div>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {(
                      t("event.results.plan.after.items", { returnObjects: true, defaultValue: [] }) as string[]
                    ).map((it, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.18 }}
              className="rounded-xl border border-border bg-muted/40 p-4"
            >
              <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Brain className="h-4 w-4 text-primary shrink-0" />
                {t("event.results.plan.personalization.title")}
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {primaryRiskKey ? t(`event.results.plan.personalization.${primaryRiskKey}`) : t("event.results.plan.personalization.none")}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: 0.22 }}
              className="flex flex-col gap-3 sm:flex-row sm:justify-end"
            >
              <Button
                size="lg"
                className="font-semibold"
                onClick={() => {
                  setPlanModalOpen(false);
                  setSendSuccess(false);
                  setSendError("");
                  setEmailFormOpen(true);
                }}
              >
                {t("event.results.plan.ctaPrimary")}
              </Button>
              <Button
                size="lg"
                variant="secondary"
                className="font-semibold"
                onClick={() => {
                  setPlanModalOpen(false);
                  setSalesModalOpen(true);
                }}
              >
                {t("event.results.plan.ctaSecondary")}
              </Button>
            </motion.div>
          </motion.div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={emailFormOpen} onOpenChange={setEmailFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("event.results.plan.form.title")}</DialogTitle>
            <DialogDescription>{t("event.results.plan.form.subtitle")}</DialogDescription>
          </DialogHeader>
          {sendSuccess ? (
            <div className="text-sm text-accent font-medium">
              {t("event.results.plan.form.success")}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="emailPro">{t("event.results.plan.form.email")}</Label>
                <Input
                  id="emailPro"
                  type="email"
                  value={emailPro}
                  onChange={(e) => setEmailPro(e.target.value)}
                  placeholder={String(t("event.results.plan.form.emailPlaceholder"))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">{t("event.results.plan.form.name")}</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={String(t("event.results.plan.form.namePlaceholder"))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companySize">{t("event.results.plan.form.companySize")}</Label>
                <Input
                  id="companySize"
                  value={companySize}
                  onChange={(e) => setCompanySize(e.target.value)}
                  placeholder={String(t("event.results.plan.form.companySizePlaceholder"))}
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-muted-foreground">
                <Checkbox checked={wantsContact} onCheckedChange={(v) => setWantsContact(v === true)} />
                <span>{t("event.results.plan.form.wantsContact")}</span>
              </label>
              {sendError && <p className="text-sm text-destructive">{sendError}</p>}
              <Button size="lg" className="w-full" disabled={sendingPlan} onClick={handleSendPlan}>
                {sendingPlan ? t("event.results.plan.form.sending") : t("event.results.plan.form.submit")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto flex items-center justify-between h-16 px-4 md:px-8">
          <BrandLogo asLink imgClassName="h-9" />
          <LanguageSwitcher />
        </div>
      </header>

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-8 max-w-3xl">
          <AnimatePresence mode="wait">
            {!started && !showResults && (
              <motion.div
                key="welcome"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center py-12"
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 }}
                  className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-8"
                >
                  <BrandLogo variant="mark" imgClassName="h-12 w-12" />
                </motion.div>

                <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-4">
                  {t("event.welcome.title")}
                </h1>
                <p className="text-lg text-primary font-semibold mb-2">
                  {t("event.welcome.subtitle")}
                </p>
                <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
                  {t("event.welcome.description")}
                </p>

                <Card className="border-primary/20 bg-card/50 backdrop-blur mb-8">
                  <CardContent className="p-6 text-left">
                    <h3 className="font-semibold text-foreground mb-3">{t("event.welcome.whatToExpect")}</h3>
                    <ul className="space-y-2 text-sm text-muted-foreground">
                      <li className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-primary shrink-0" />
                        {t("event.welcome.point1")}
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-primary shrink-0" />
                        {t("event.welcome.point2")}
                      </li>
                      <li className="flex items-center gap-2">
                        <ChevronRight className="h-4 w-4 text-primary shrink-0" />
                        {t("event.welcome.point3")}
                      </li>
                    </ul>
                  </CardContent>
                </Card>

                <Button
                  size="lg"
                  className="gap-2 font-semibold px-10"
                  onClick={() => {
                    void trackAnalyticsEvent("resilience_test_started", "event");
                    setStarted(true);
                  }}
                >
                  {t("event.welcome.startTest")}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </motion.div>
            )}

            {started && !showResults && (
              <motion.div
                key={`question-${currentQ}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
                className="py-12"
              >
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t("event.test.question")} {currentQ + 1}/5
                    </span>
                    <span className="text-xs text-muted-foreground">{Math.round(((currentQ + 1) / 5) * 100)}%</span>
                  </div>
                  <Progress value={((currentQ + 1) / 5) * 100} className="h-2" />
                </div>

                <Card className="border-border">
                  <CardContent className="p-8 md:p-10">
                    <h2 className="font-display text-xl md:text-2xl font-bold text-foreground mb-8">
                      {t(`event.test.${QUESTIONS_KEYS[currentQ]}`)}
                    </h2>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        size="lg"
                        variant={answers[currentQ] === "yes" ? "default" : "outline"}
                        className="flex-1 gap-2 text-base"
                        onClick={() => handleAnswer("yes")}
                      >
                        <CheckCircle2 className="h-5 w-5" />
                        {t("event.test.yes")}
                      </Button>
                      <Button
                        size="lg"
                        variant={answers[currentQ] === "no" ? "default" : "outline"}
                        className="flex-1 gap-2 text-base"
                        onClick={() => handleAnswer("no")}
                      >
                        <AlertTriangle className="h-5 w-5" />
                        {t("event.test.no")}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {showResults && (
              <motion.div
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="py-12"
              >
                <div className="text-center mb-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.2 }}
                    className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${
                      isAtRisk ? "bg-destructive/10" : "bg-accent/10"
                    }`}
                  >
                    {isAtRisk ? (
                      <AlertTriangle className="h-12 w-12 text-destructive" />
                    ) : (
                      <CheckCircle2 className="h-12 w-12 text-accent" />
                    )}
                  </motion.div>

                  <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                    {isAtRisk ? t("event.results.atRiskTitle") : t("event.results.safeTitle")}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {isAtRisk ? t("event.results.atRiskDescription") : t("event.results.safeDescription")}
                  </p>
                </div>

                {/* Score card */}
                <Card className="mb-6 border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-foreground">{t("event.results.resiliencyScore")}</span>
                      <span className={`text-2xl font-bold ${isAtRisk ? "text-destructive" : "text-accent"}`}>
                        {scorePercent}%
                      </span>
                    </div>
                    <Progress value={scorePercent} className="h-3" />
                    <div
                      className={[
                        "mt-4 rounded-xl border p-4 whitespace-pre-line",
                        isAtRisk
                          ? "border-destructive/30 bg-destructive/10 text-destructive"
                          : "border-accent/30 bg-accent/10 text-accent",
                      ].join(" ")}
                    >
                      <div className="text-base font-semibold leading-snug">
                        {isAtRisk ? t("event.results.consequence.low") : t("event.results.consequence.high")}
                      </div>
                    </div>
                    {isAtRisk && (
                      <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
                        {(
                          t("event.results.riskImpacts", {
                            returnObjects: true,
                            defaultValue: [],
                          }) as string[]
                        ).map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                </Card>

                {/*
                  Answer summary (hidden to keep results screen simpler).
                  Keeping this block here in case we want to re-enable it later.
                */}
                {/* <Card className="mb-8 border-border">
                  <CardContent className="p-6 space-y-3">
                    {QUESTIONS_KEYS.map((key, i) => {
                      const a = answers[i];
                      const feedbackKey = FEEDBACK_KEYS[i];
                      const variant = a === "yes" ? "yes" : "no";
                      return (
                        <div key={key} className="flex items-start gap-3 text-sm">
                          {a === "yes" ? (
                            <CheckCircle2 className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                          ) : (
                            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                          )}
                          <div className="text-muted-foreground/90 whitespace-pre-line">
                            {t(`event.results.feedback.${feedbackKey}.${variant}`)}
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card> */}

                {/* CTAs */}
                <div className="flex flex-col gap-4 justify-center items-center">
                  <Button
                    size="lg"
                    className="gap-2 font-bold text-base md:text-lg h-14 px-8 sm:px-12"
                    onClick={() => setPlanModalOpen(true)}
                  >
                    {t("event.results.seeHowTo100")}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  {isAtRisk && (
                    <Button size="lg" className="gap-2 font-semibold" onClick={() => setSalesModalOpen(true)}>
                      {t("event.results.bookCall")}
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="lg" variant="outline" onClick={reset}>
                    {t("event.results.retake")}
                  </Button>
                </div>

                {!isAtRisk && (
                  <p className="text-center text-sm text-muted-foreground mt-6">
                    {t("event.results.safeNote")}
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default Event;
